<template>
	<v-container
		fluid
	>
		<v-row>
			<v-col
				xs12
				lg8
			>
				<v-card>
					<v-card-title primary-title>
						<v-row>
							<v-col>
								<div
									v-if="title !== undefined"
									class="text-h5"
								>
									<v-icon
										size="large"
										start
										icon="$export"
									/>
									<translate>{{ title }}</translate>
								</div>
								<div>
									<span
										v-if="subtitle !== undefined"
										class="text-grey text-body-2"
									>
										<translate>{{ subtitle }}</translate>
									</span>
								</div>
							</v-col>
						</v-row>
					</v-card-title>
					<v-card-text>
						<v-row
							v-if="definitions.length > 0"
							class="xs12 sm6"
						>
							<v-col>
								<v-data-table
									class="justify-center"
									density="compact"
									:headers="descriptionTableHeaders"
									:items="definitions"
								>
									<thead>
										<tr>
											<th class="text-left">
												<translate>Variable</translate>
											</th>
											<th class="text-left">
												<translate>Definition</translate>
											</th>
											<th class="text-left">
												<translate>Assumptions</translate>
											</th>
										</tr>
									</thead>
									<tbody>
										<tr
											v-for="definition in definitions"
											:key="definition.variable"
										>
											<td>{{ definition.variable }}</td>
											<td v-if="definition.description">
												<!-- eslint-disable-next-line vue/no-v-html value not injected, so it's safe-->
												<p v-html="$gettext(definition.description)" />
											</td>
											<td v-if="definition.assumption">
												{{ $gettext(definition.assumption) }}
											</td>
										</tr>
									</tbody>
									<template #bottom />
								</v-data-table>
							</v-col>
						</v-row>
						<v-container
							fluid
							grid-list-lg
						>
							<v-row>
								<v-col
									v-if="startDate"
									sm="6"
								>
									<date-text-field
										v-model="startDateValue"
										:label="$gettext(startName)"
										name="start_date"
										variant="outlined"
										:error-messages="errors.startDate"
										:model-value="startDateValue"
									/>
								</v-col>
								<v-col
									v-if="endDate"
									sm="6"
								>
									<date-text-field
										v-model="endDateValue"
										:label="$gettext(endName)"
										name="end_date"
										variant="outlined"
										:error-messages="errors.endDate"
										:model-value="endDateValue"
									/>
								</v-col>
								<v-col
									v-if="monthDate"
									sm="6"
								>
									<month-text-field
										v-model="monthValue"
										:label="$gettext('Month')"
										name="month"
										variant="outlined"
										:error-messages="errors.month"
									/>
								</v-col>
								<v-col
									v-if="products.length > 0"
									class="pt-0"
									sm="6"
								>
									<v-autocomplete
										v-model="productsValue"
										:label="$gettext('Select Products')"
										:items="products"
										item-title="name"
										item-value="id"
										chips
										closable-chips
										multiple
										variant="outlined"
									/>
								</v-col>
								<v-col
									v-if="radioOptions !== undefined"
									xs12
									sm6
								>
									<v-radio-group
										v-model="endStatus"
										mandatory
									>
										<template #label>
											<div><translate>{{ radioTitle }}</translate></div>
										</template>
										<v-radio
											v-for="(option, index) in radioOptions"
											:key="index"
											:label="$gettext(option.label)"
											:value="option.value"
										/>
									</v-radio-group>
								</v-col>
							</v-row>
						</v-container>
					</v-card-text>
					<v-card-actions class="">
						<v-btn
							:loading="loading"
							color="primary"
							@click="requestExport"
						>
							<v-icon
								start
								icon="$export"
							/>
							<translate>Download report</translate>
						</v-btn>
						<v-btn
							v-if="show"
							:loading="loading"
							color="primary"
							@click="showReport"
						>
							<v-icon
								start
								icon="$export"
							/>
							<translate>Show report</translate>
						</v-btn>
					</v-card-actions>
				</v-card>
			</v-col>
		</v-row>
		<v-row>
			<v-col
				xs12
				lg8
			>
				<v-card v-if="tableEndpoint">
					<model-backend-table
						v-model="data"
						v-model:options="options"
						density="compact"
						:endpoint="tableEndpoint"
						:headers="tableHeader"
						:server-items-length="totalItems"
						:filter="filter"
						:items-per-page-options="[ itemsPerPage, 50, 100, ]"
					>
						<template
							v-for="(_, slot) of $slots"
							#[slot]="scope"
						>
							<slot
								:name="slot"
								v-bind="scope"
							/>
						</template>
					</model-backend-table>
				</v-card>
			</v-col>
		</v-row>
	</v-container>
</template>

<script>
import {
	axios,
} from '../../services/axios-service.js';
import ModelBackendTable from '../molecules/ModelBackendTable.vue';
import DateTextField from '../atoms/DateTextField.vue';
import MonthTextField from '../atoms/MonthTextField.vue';

export default {
	name: 'ReportPage',
	components: {
		MonthTextField,
		DateTextField, ModelBackendTable
	},
	props: {
		startDate: {
			type: Boolean,
			default: false,
		},
		startName: {
			type: String,
			default: 'Start date'
		},
		endDate: {
			type: Boolean,
			default: false,
		},
		endName: {
			type: String,
			default: 'End date'
		},
		monthDate: {
			type: Boolean,
			default: false,
		},
		products: {
			type: Array,
			default: () => [],
			validator: (val) => {
				for (const product of val) {
					if (
						product.id === undefined ||
						product.name === undefined
					) {
						return false;
					}
				}
				return true;
			},
		},
		radioTitle: {
			type: String,
			default: undefined,
		},
		radioOptions: {
			type: Array,
			default: undefined,
		},
		getUrl: {
			type: String,
			default: undefined,
		},
		title: {
			type: String,
			default: undefined,
		},
		subtitle: {
			type: String,
			default: undefined,
		},
		exportFilename: {
			type: String,
			default: 'export',
		},
		itemsPerPage: {
			type: Number,
			default: 10
		},
		show: {
			type: Boolean,
			default: true
		}
	},
	data () {
		return {
			errors: {
				startDate: [],
				endDate: [],
				month: [],
				products: [],
			},
			loading: false,
			file: undefined,
			data: [],
			startDateValue: null,
			endDateValue: null,
			monthValue: null,
			productsValue: null,
			tableHeader: [],
			definitions: [],
			tableEndpoint: '',
			totalItems: 0,
			options: {
			},
			filter: {
			},
			endStatus: undefined,
		};
	},

	computed: {
		/**
		 * @returns {string}
		 */
		getTableUrl: function () {
			return this.getUrl.concat('/table');
		},

		descriptionTableHeaders () {
			return [
				{
					title: 'Variable', value: 'variable', sortable: false,
				},
				{
					title: 'Definition', value: 'description', sortable: false,
				},
				{
					title: 'Assumptions', value: 'assumption', sortable: false,
				},
			];
		},
	},

	created () {
		if (this.startDate) {
			const date = new Date();
			let month = date.getMonth();
			let year = date.getFullYear();
			const dayDate = new Date(year, month, 0);

			if (this.endDate) {
				if (month === 0) {
					month = 12;
					year = year - 1;
				}
				if (month < 10) {
					month = '0' + month ;
				}
				this.startDateValue = [year, month, '01', ].join('-');
				this.endDateValue = [year, month, dayDate.getDate(), ].join('-');
			} else {
				month = month + 1;
				if (month < 10) {
					month = '0' + month;
				}
				let day = date.getDate();

				if (day < 10) {
					day = '0' + day;
				}
				this.startDateValue = [year, month, day, ].join('-');
			}
		}
		if (this.monthDate) {
			const date = new Date();
			let month = date.getMonth();
			const year = date.getFullYear();

			if (month < 10) {
				month = '0' + month ;
			}

			this.monthValue = [year, month, ].join('-');
		}
		if (this.products) {
			this.productsValue = this.products.map((product) => product.id);
		}

		if (this.radioOptions) {
			this.endStatus = this.radioOptions[0].value;
		}
	},

	mounted () {
		this.getTableHeader();
		this.getDefinitions();
	},

	methods: {

		/**
		 * Set the filter values for the table and set the endpoint
		 */
		showReport: function () {
			if (this.monthDate) {
				this.determineMonthDates();
			}
			this.filter = {
				'start_date': this.startDateValue,
				'end_date': this.endDateValue,
				'products': this.productsValue,
				'end_status': this.endStatus
			};
			this.tableEndpoint = this.getUrl.concat('/table');
		},

		/**
		 * Requests the header for the table
		 *
		 * @returns {void}
		 */
		getTableHeader () {
			axios.get(this.getUrl.concat('/header'))
				.then(({
					data,
				}) => {
					this.tableHeader = data;
				}).catch((error) => {
					window.captureException(error);
				});
		},

		/**
		 * Requests the definitions of the report
		 *
		 * @returns {void}
		 */
		getDefinitions () {
			axios.get(this.getUrl.concat('/definitions'))
				.then(({
					data,
				}) => {
					this.definitions = data;
				}).catch((error) => {
					window.captureException(error);
				});
		},

		/**
		 * Determine the start and end date of the specified month
		 *
		 * @returns {void}
		 */
		determineMonthDates () {
			const [year, month, ] = this.monthValue.split('-');
			const lastMonthDate = new Date(year, month, 0);

			this.startDateValue = [year, month, '01', ].join('-');
			this.endDateValue = [year, month, lastMonthDate.getDate(), ].join('-');

		},

		/**
		 * Requests an export from the server and when it is returned makes it downloadable, otherwise shows the errors for the selection
		 *
		 * @returns {void}
		 * @protected
		 */
		requestExport () {
			this.loading = true;
			this.file = undefined;
			if (this.monthDate) {
				this.determineMonthDates();
			}
			axios.get(this.getUrl.concat('/export'), {
				params: {
					startDate: this.startDateValue,
					endDate: this.endDateValue,
					products: this.productsValue,
					endStatus: this.endStatus,
				}
			})
				.then((response) => {
					this.loading = false;
					this.file = response.data;
					this.errors = {
						startDate: [],
						endDate: [],
						products: [],
					};
					if (this.file !== undefined) {
						this.downloadFile();
					}
				})
				.catch((response) => {
					this.loading = false;
					if (response.response.data) {
						this.errors = response.response.data.errors;
					} else {
						this.errors = {
							startDate: ['An unexpected error has occurred', ],
							endDate: [],
							products: [],
						};
					}
				});
		},

		/**
		 * Downloads the file blob
		 * Needs to create an actual DOM element in order to make it possible to download from the window
		 * This immediately removes it.
		 *
		 * @returns {void}
		 * @protected
		 */
		downloadFile () {
			const blob = new Blob([this.file, ], {
				type: 'text/csv',
			});

			const link = document.createElement('a');

			link.href = window.URL.createObjectURL(blob);
			link.download = this.exportFilename;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		},
	},
};
</script>
